import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Form, FormGroup, Input, Label } from "reactstrap";
import { toast } from "react-toastify";
import UserContext from "./UserContext";
import ValetApi from "../api/Api";

const MANUAL_METHOD_OPTIONS = [
    { value: "cash", label: "Cash" },
    { value: "void", label: "Void / Waived" },
];

const ALLOWED_MANUAL_METHOD_VALUES = new Set(MANUAL_METHOD_OPTIONS.map((option) => option.value));

function buildDefaultForm() {
    return {
        isEnabled: false,
        requirePaymentBeforeRequest: false,
        paymentModel: "flat_rate",
        currency: "usd",
        flatRateCents: 0,
        freeMinutes: 15,
        incrementMinutes: 15,
        incrementCents: 500,
        dailyMaxCents: null,
        manualMethods: ["cash"],
        timeTableJsonText: '[\n  {"upToMinutes":30,"amountCents":0},\n  {"upToMinutes":120,"amountCents":1200}\n]',
    };
}

function mapConfigToForm(config) {
    if (!config) return buildDefaultForm();

    const normalizedManualMethods = Array.isArray(config.manualMethods)
        ? config.manualMethods
              .map((method) =>
                  String(method || "")
                      .trim()
                      .toLowerCase(),
              )
              .filter((method) => ALLOWED_MANUAL_METHOD_VALUES.has(method))
        : [];

    return {
        isEnabled: Boolean(config.isEnabled),
        requirePaymentBeforeRequest: Boolean(config.requirePaymentBeforeRequest),
        paymentModel: config.paymentModel || "flat_rate",
        currency: String(config.currency || "usd").toLowerCase(),
        flatRateCents: Number.isInteger(Number(config.flatRateCents)) ? Number(config.flatRateCents) : 0,
        freeMinutes: Number.isInteger(Number(config.freeMinutes)) ? Number(config.freeMinutes) : 15,
        incrementMinutes: Number.isInteger(Number(config.incrementMinutes)) ? Number(config.incrementMinutes) : 15,
        incrementCents: Number.isInteger(Number(config.incrementCents)) ? Number(config.incrementCents) : 500,
        dailyMaxCents: Number.isInteger(Number(config.dailyMaxCents)) ? Number(config.dailyMaxCents) : null,
        manualMethods: normalizedManualMethods.length ? Array.from(new Set(normalizedManualMethods)) : ["cash"],
        timeTableJsonText: JSON.stringify(Array.isArray(config.timeTableJson) ? config.timeTableJson : [], null, 2),
    };
}

function formatCents(cents) {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

const PaymentConfigPanel = () => {
    const { userDetail, activePodiumId } = useContext(UserContext);
    const [selectedLocationId, setSelectedLocationId] = useState("");
    const [selectedPodiumId, setSelectedPodiumId] = useState("");
    const [isLoadingConfig, setIsLoadingConfig] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [existingConfig, setExistingConfig] = useState(null);
    const [form, setForm] = useState(buildDefaultForm());

    const accessibleLocations = useMemo(() => {
        const explicitLocations = Array.isArray(userDetail?.accessibleLocations) ? userDetail.accessibleLocations : [];
        if (explicitLocations.length) {
            return explicitLocations
                .map((l) => ({
                    id: Number(l.id),
                    name: l.name,
                }))
                .filter((l) => Number.isInteger(l.id));
        }

        const podiums = Array.isArray(userDetail?.accessiblePodiums) ? userDetail.accessiblePodiums : [];
        const locationIds = Array.from(new Set(podiums.map((p) => Number(p.locationId)).filter((id) => Number.isInteger(id) && id > 0)));
        return locationIds.map((id) => ({ id, name: `Location ${id}` }));
    }, [userDetail]);

    const accessiblePodiums = useMemo(() => {
        const podiums = Array.isArray(userDetail?.accessiblePodiums) ? userDetail.accessiblePodiums : [];
        return podiums.map((p) => ({
            id: Number(p.id),
            name: p.name,
            locationId: Number(p.locationId || 0),
        }));
    }, [userDetail]);

    useEffect(() => {
        if (!accessiblePodiums.length) {
            setSelectedLocationId("");
            setSelectedPodiumId("");
            return;
        }

        const activeId = Number(activePodiumId || 0);
        const hasActive = accessiblePodiums.some((p) => p.id === activeId);
        const fallbackPodium = hasActive ? accessiblePodiums.find((p) => p.id === activeId) : accessiblePodiums[0];

        setSelectedLocationId(String(Number(fallbackPodium?.locationId || 0) || ""));
        setSelectedPodiumId(String(Number(fallbackPodium?.id || 0) || ""));
    }, [accessiblePodiums, activePodiumId]);

    const locationScopedPodiums = useMemo(() => {
        if (!selectedLocationId) return [];
        return accessiblePodiums.filter((p) => String(p.locationId) === String(selectedLocationId));
    }, [accessiblePodiums, selectedLocationId]);

    useEffect(() => {
        async function loadConfig() {
            if (!selectedPodiumId) {
                setExistingConfig(null);
                setForm(buildDefaultForm());
                return;
            }

            try {
                setIsLoadingConfig(true);
                const res = await ValetApi.getPaymentConfigByPodium(Number(selectedPodiumId));
                const config = res?.data?.config || null;
                setExistingConfig(config);
                setForm(mapConfigToForm(config));
            } catch (err) {
                setExistingConfig(null);
                setForm(buildDefaultForm());
                toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load payment config", { autoClose: 1200 });
            } finally {
                setIsLoadingConfig(false);
            }
        }

        loadConfig();
    }, [selectedPodiumId]);

    function updateField(field, value) {
        setForm((prev) => ({ ...prev, [field]: value }));
    }

    function setManualMethodChecked(method, isChecked) {
        setForm((prev) => {
            const existing = Array.isArray(prev.manualMethods) ? prev.manualMethods : ["cash"];
            const hasMethod = existing.includes(method);
            const isRemoving = hasMethod && !isChecked;
            const isAdding = !hasMethod && isChecked;

            if (!isRemoving && !isAdding) {
                return {
                    ...prev,
                    manualMethods: [...existing],
                };
            }

            if (isRemoving && existing.length === 1) {
                toast.info("At least one in-person payment method must remain enabled.", { autoClose: 1200 });
                return {
                    ...prev,
                    manualMethods: [...existing],
                };
            }

            const next = isRemoving ? existing.filter((m) => m !== method) : [...existing, method];
            return {
                ...prev,
                manualMethods: Array.from(new Set(next)),
            };
        });
    }

    function parseTimeTableOrThrow(rawText) {
        if (!rawText.trim()) return [];
        const parsed = JSON.parse(rawText);
        if (!Array.isArray(parsed)) {
            throw new Error("Time table must be a JSON array");
        }
        return parsed;
    }

    async function handleSave(evt) {
        evt.preventDefault();
        if (!selectedPodiumId) {
            toast.error("Choose a podium before saving", { autoClose: 1200 });
            return;
        }

        let parsedTimeTable = [];
        if (form.paymentModel === "time_table") {
            try {
                parsedTimeTable = parseTimeTableOrThrow(form.timeTableJsonText);
            } catch {
                toast.error("Time table JSON is invalid", { autoClose: 1200 });
                return;
            }
        }

        const payload = {
            isEnabled: Boolean(form.isEnabled),
            requirePaymentBeforeRequest: Boolean(form.requirePaymentBeforeRequest),
            paymentModel: form.paymentModel,
            currency: String(form.currency || "usd").toLowerCase(),
            flatRateCents: form.paymentModel === "flat_rate" ? Number(form.flatRateCents || 0) : null,
            freeMinutes: form.paymentModel === "time_increment" ? Number(form.freeMinutes || 0) : null,
            incrementMinutes: form.paymentModel === "time_increment" ? Number(form.incrementMinutes || 15) : null,
            incrementCents: form.paymentModel === "time_increment" ? Number(form.incrementCents || 0) : null,
            dailyMaxCents: form.paymentModel === "time_increment" && Number.isInteger(Number(form.dailyMaxCents)) ? Number(form.dailyMaxCents) : null,
            timeTableJson: form.paymentModel === "time_table" ? parsedTimeTable : [],
            manualMethods: Array.isArray(form.manualMethods) && form.manualMethods.length ? form.manualMethods : ["cash"],
        };

        try {
            setIsSaving(true);
            const res = await ValetApi.upsertPaymentConfigByPodium(Number(selectedPodiumId), payload);
            const saved = res?.data?.config || null;
            setExistingConfig(saved);
            setForm(mapConfigToForm(saved));
            toast.success("Payment configuration saved", { autoClose: 1200 });
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to save payment config", { autoClose: 1600 });
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <div className="payment-config-panel">
            <div className="payment-config-row">
                <Label for="paymentConfigLocation">Location</Label>
                <Input
                    id="paymentConfigLocation"
                    type="select"
                    value={selectedLocationId}
                    onChange={(e) => {
                        const nextLocationId = e.target.value;
                        setSelectedLocationId(nextLocationId);

                        const scoped = accessiblePodiums.filter((p) => String(p.locationId) === String(nextLocationId));
                        setSelectedPodiumId(scoped.length ? String(scoped[0].id) : "");
                    }}>
                    {accessibleLocations.length ? null : <option value="">No locations available</option>}
                    {accessibleLocations.map((location) => (
                        <option
                            key={location.id}
                            value={location.id}>
                            {location.name}
                        </option>
                    ))}
                </Input>
            </div>

            <div className="payment-config-row">
                <Label for="paymentConfigPodium">Podium</Label>
                <Input
                    id="paymentConfigPodium"
                    type="select"
                    value={selectedPodiumId}
                    onChange={(e) => setSelectedPodiumId(e.target.value)}>
                    {locationScopedPodiums.length ? null : <option value="">No podiums available for selected location</option>}
                    {locationScopedPodiums.map((podium) => (
                        <option
                            key={podium.id}
                            value={podium.id}>
                            {podium.name}
                        </option>
                    ))}
                </Input>
            </div>

            {isLoadingConfig ? (
                <p>Loading pricing config...</p>
            ) : (
                <Form onSubmit={handleSave}>
                    <FormGroup
                        check
                        className="payment-toggle-row">
                        <input
                            id="paymentConfigEnabled"
                            type="checkbox"
                            className="payment-toggle-checkbox"
                            checked={form.isEnabled}
                            onChange={(e) => updateField("isEnabled", e.target.checked)}
                        />
                        <label
                            className="form-check-label payment-toggle-label"
                            htmlFor="paymentConfigEnabled">
                            Enable payment at this podium
                        </label>
                    </FormGroup>

                    <FormGroup
                        check
                        className="payment-toggle-row">
                        <input
                            id="paymentConfigRequireBeforeRequest"
                            type="checkbox"
                            className="payment-toggle-checkbox"
                            checked={form.requirePaymentBeforeRequest}
                            onChange={(e) => updateField("requirePaymentBeforeRequest", e.target.checked)}
                        />
                        <label
                            className="form-check-label payment-toggle-label"
                            htmlFor="paymentConfigRequireBeforeRequest">
                            Require payment before request
                        </label>
                    </FormGroup>

                    <FormGroup>
                        <div className="form-label">Accept in-person payment methods (valet fallback)</div>
                        <div className="d-flex flex-wrap gap-3">
                            {MANUAL_METHOD_OPTIONS.map((option) => {
                                const selectedMethods = Array.isArray(form.manualMethods) ? form.manualMethods : [];
                                const isChecked = selectedMethods.includes(option.value);

                                return (
                                    <div
                                        key={option.value}
                                        className="form-check payment-method-option mr-3">
                                        <input
                                            id={`manualMethod-${option.value}`}
                                            type="checkbox"
                                            className="payment-method-checkbox"
                                            checked={isChecked}
                                            onChange={(e) => setManualMethodChecked(option.value, e.target.checked)}
                                        />
                                        <label
                                            className="form-check-label payment-method-label"
                                            htmlFor={`manualMethod-${option.value}`}>
                                            {option.label}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                        <small>Use cash for collected payment or void when payment is waived.</small>
                    </FormGroup>

                    <div className="payment-config-grid">
                        <FormGroup>
                            <Label for="paymentModel">Pricing model</Label>
                            <Input
                                id="paymentModel"
                                type="select"
                                value={form.paymentModel}
                                onChange={(e) => updateField("paymentModel", e.target.value)}>
                                <option value="flat_rate">Flat rate</option>
                                <option value="time_increment">Time increment</option>
                                <option value="time_table">Time table</option>
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label for="paymentCurrency">Currency (ISO)</Label>
                            <Input
                                id="paymentCurrency"
                                value={form.currency}
                                maxLength={3}
                                onChange={(e) => updateField("currency", e.target.value.toLowerCase())}
                            />
                        </FormGroup>
                    </div>

                    {form.paymentModel === "flat_rate" ? (
                        <FormGroup>
                            <Label for="flatRateCents">Flat rate (cents)</Label>
                            <Input
                                id="flatRateCents"
                                type="number"
                                min="0"
                                value={form.flatRateCents}
                                onChange={(e) => updateField("flatRateCents", e.target.value)}
                            />
                            <small>Current amount: {formatCents(form.flatRateCents)}</small>
                        </FormGroup>
                    ) : null}

                    {form.paymentModel === "time_increment" ? (
                        <div className="payment-config-grid payment-config-grid-three">
                            <FormGroup>
                                <Label for="freeMinutes">Free minutes</Label>
                                <Input
                                    id="freeMinutes"
                                    type="number"
                                    min="0"
                                    value={form.freeMinutes}
                                    onChange={(e) => updateField("freeMinutes", e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="incrementMinutes">Increment minutes</Label>
                                <Input
                                    id="incrementMinutes"
                                    type="number"
                                    min="1"
                                    value={form.incrementMinutes}
                                    onChange={(e) => updateField("incrementMinutes", e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="incrementCents">Increment cents</Label>
                                <Input
                                    id="incrementCents"
                                    type="number"
                                    min="0"
                                    value={form.incrementCents}
                                    onChange={(e) => updateField("incrementCents", e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="dailyMaxCents">Daily max cents (optional)</Label>
                                <Input
                                    id="dailyMaxCents"
                                    type="number"
                                    min="0"
                                    value={form.dailyMaxCents ?? ""}
                                    onChange={(e) => updateField("dailyMaxCents", e.target.value === "" ? null : e.target.value)}
                                />
                            </FormGroup>
                        </div>
                    ) : null}

                    {form.paymentModel === "time_table" ? (
                        <FormGroup>
                            <Label for="timeTableJsonText">Time table JSON</Label>
                            <Input
                                id="timeTableJsonText"
                                type="textarea"
                                rows="7"
                                value={form.timeTableJsonText}
                                onChange={(e) => updateField("timeTableJsonText", e.target.value)}
                            />
                            <small>Each row needs upToMinutes and amountCents.</small>
                        </FormGroup>
                    ) : null}

                    {existingConfig ? <div className="payment-config-meta">Last updated: {new Date(existingConfig.updatedAt).toLocaleString()}</div> : <div className="payment-config-meta">No config saved yet for this podium.</div>}

                    <Button
                        color="primary"
                        type="submit"
                        disabled={isSaving || !selectedPodiumId}>
                        {isSaving ? "Saving..." : "Save Payment Config"}
                    </Button>
                </Form>
            )}
        </div>
    );
};

export default PaymentConfigPanel;
